import React, { useState } from 'react';
import { DialogField } from '@/lib/stores/dialogStore';
import { IconProvider } from '@/components/ui';
import { Button } from '@/components/ui/button';
import { TextAreaElement } from '@/components/wizards/wizard-elements/TextAreaElement';
import { getCachedTypes } from '@/lib/stores/typesStore';
import { getCachedObjects } from '@/lib/stores/objectsStore';

interface ConditionEvalResult {
  variableName: string;
  variableValue: boolean | number | string;
  matchedOperator?: string;
  matchedValue?: boolean | number | string;
  matchedLabel?: string;
  matched: boolean;
}

interface SetterEvalResult {
  variableName: string;
  operator: string;
  setValue: boolean | number | string;
  newValue: boolean | number | string;
}

interface DialogFieldPrevDisplayProps {
  field: DialogField;
  sectionIndex: number;
  speakerMode?: 'none' | 'type' | 'free-text' | 'object';
  speakerTypeId?: string | null;
  gameId?: string;
  onUpdateField: (sectionIndex: number, fieldId: string, updates: Partial<DialogField>) => void;
  conditionResult?: ConditionEvalResult;
  setterResult?: SetterEvalResult;
}

export function DialogFieldPrevDisplay({
  field,
  sectionIndex,
  speakerMode = 'none',
  speakerTypeId,
  gameId,
  onUpdateField,
  conditionResult,
  setterResult,
}: DialogFieldPrevDisplayProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(field.text);
  
  // Get speaker display name (only for 'dialog', 'question', and 'end-dialog' types)
  const getSpeakerName = (): string | null => {
    if (!field.speaker || speakerMode === 'none') return null;
    if (field.type !== 'dialog' && field.type !== 'question' && field.type !== 'end-dialog') return null;
    
    if (speakerMode === 'free-text') {
      return field.speaker;
    } else if (speakerMode === 'type' && gameId) {
      const types = getCachedTypes(gameId);
      const selectedType = speakerTypeId ? types.find(t => t.id === speakerTypeId) : null;
      const element = selectedType?.data?.find(e => e.id === field.speaker);
      return element?.name || null;
    } else if (speakerMode === 'object' && gameId) {
      const objects = getCachedObjects(gameId);
      const obj = objects.find(o => o.id === field.speaker);
      return obj?.name || null;
    }
    
    return null;
  };
  
  const speakerName = getSpeakerName();

  const handleSaveEdit = () => {
    onUpdateField(sectionIndex, field.id, { text: editValue });
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setEditValue(field.text);
    setIsEditing(false);
  };

  return (
    <div className="group">
      {/* Field text display */}
      <div className="relative flex items-center">
        {isEditing ? (
          <div className="flex flex-col gap-3 w-full">
            <div className="w-full">
              <TextAreaElement
                value={editValue}
                onChange={setEditValue}
                placeholder="Enter text..."
                rows={1}
                enableBubbleMenu={true}
              />
            </div>
            <div className="flex flex-row gap-2 justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancelEdit}
                type="button"
              >
                Cancel
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleSaveEdit}
                type="button"
              >
                Save
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* Speaker name */}
            {speakerName && (
              <div className="text-xs font-semibold text-primary italic mr-2">
                {speakerName}:
              </div>
            )}
            
            {/* Field text */}
            <div
              className={`text-sm leading-relaxed text-gray-800 text-left ${field.type === 'context' ? 'italic' : ''}`}
              dangerouslySetInnerHTML={{ __html: field.text }}
            />
            
            {/* Edit icon - appears on hover */}
            <button
              onClick={() => {
                setEditValue(field.text);
                setIsEditing(true);
              }}
              className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-2 hover:bg-gray-100 rounded-md"
              title="Edit text"
              type="button"
            >
              <IconProvider iconstring="fa-edit" size={14} className="text-gray-500" />
            </button>
          </>
        )}
      </div>

      {/* Condition result display */}
      {field.type === 'condition' && conditionResult && (
        <div className="mx-0 mt-1">
          <div className={`border rounded-lg px-3 py-2 text-xs ${conditionResult.matched ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'}`}>
            <div className="flex items-center gap-2">
              <IconProvider iconstring="fa-code-branch" size={12} className={conditionResult.matched ? 'text-emerald-600' : 'text-red-500'} />
              <span className="font-mono text-gray-700">
                {conditionResult.variableName} = <span className="font-bold">{String(conditionResult.variableValue)}</span>
              </span>
              {conditionResult.matched && conditionResult.matchedOperator && (
                <>
                  <span className="text-gray-400">&#8594;</span>
                  <span className={`font-mono font-semibold ${conditionResult.matched ? 'text-emerald-700' : 'text-red-600'}`}>
                    {conditionResult.matchedOperator === 'in_range' ? 'in' : conditionResult.matchedOperator}{' '}
                    {conditionResult.matchedLabel || String(conditionResult.matchedValue)}
                  </span>
                  <IconProvider iconstring="fa-check" size={10} className="text-emerald-600" />
                </>
              )}
              {!conditionResult.matched && (
                <>
                  <span className="text-gray-400">&#8594;</span>
                  <span className="font-mono text-red-600">no match</span>
                  <IconProvider iconstring="fa-xmark" size={10} className="text-red-500" />
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Setter result display */}
      {field.type === 'setter' && setterResult && (
        <div className="mx-0 mt-1">
          <div className="border border-emerald-200 bg-emerald-50 rounded-lg px-3 py-2 text-xs">
            <div className="flex items-center gap-2">
              <IconProvider iconstring="fa-pen" size={12} className="text-emerald-600" />
              <span className="font-mono text-gray-700">
                {setterResult.variableName}
              </span>
              <span className="text-emerald-600">
                {setterResult.operator === 'set' ? '←' : setterResult.operator === 'increment' ? '+=' : '-='}
              </span>
              <span className="font-mono font-bold text-emerald-800">{String(setterResult.setValue)}</span>
              <span className="text-gray-400">&#8594;</span>
              <span className="font-mono font-semibold text-emerald-700">{String(setterResult.newValue)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}